export const SUPPORTED_LANGUAGES = ["en", "ar", "es", "ru", "he", "hi"] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = "en";

export const LANGUAGE_LABELS: Record<Language, { label: string; nativeLabel: string }> = {
    en: { label: "English", nativeLabel: "English" },
    ar: { label: "Arabic", nativeLabel: "العربية" },
    es: { label: "Spanish", nativeLabel: "Español" },
    ru: { label: "Russian", nativeLabel: "Русский" },
    he: { label: "Hebrew", nativeLabel: "עברית" },
    hi: { label: "Hindi", nativeLabel: "हिन्दी" },
};

export const OPEN_GRAPH_LOCALES: Record<Language, string> = {
    en: "en_US",
    ar: "ar_SA",
    es: "es_ES",
    ru: "ru_RU",
    he: "he_IL",
    hi: "hi_IN",
};

export const SCHEMA_LANGUAGES: Record<Language, string> = {
    en: "en-US",
    ar: "ar-SA",
    es: "es-ES",
    ru: "ru-RU",
    he: "he-IL",
    hi: "hi-IN",
};

export const getLanguageDirection = (lang: Language): "ltr" | "rtl" => {
    if (lang === "ar" || lang === "he") {
        return "rtl";
    }

    return "ltr";
};

export const isSupportedLanguage = (value?: string | null): value is Language => {
    return Boolean(value && SUPPORTED_LANGUAGES.includes(value as Language));
};

export const normalizeLanguage = (value?: string | null): Language => {
    if (!value) {
        return DEFAULT_LANGUAGE;
    }

    const normalized = value.toLowerCase().split("-")[0];

    if (isSupportedLanguage(normalized)) {
        return normalized;
    }

    return DEFAULT_LANGUAGE;
};

export const getLanguageFromPath = (pathname: string): Language | null => {
    const segments = pathname.split("/").filter(Boolean);
    const candidate = segments[0];

    if (isSupportedLanguage(candidate)) {
        return candidate;
    }

    return null;
};

export const stripLanguageFromPath = (pathname: string): string => {
    const segments = pathname.split("/").filter(Boolean);

    if (segments.length === 0) {
        return "/";
    }

    if (isSupportedLanguage(segments[0])) {
        const remainder = segments.slice(1).join("/");
        return remainder ? `/${remainder}` : "/";
    }

    return pathname.startsWith("/") ? pathname : `/${pathname}`;
};

export const buildLocalizedPath = (pathname: string, lang: Language): string => {
    const stripped = stripLanguageFromPath(pathname);
    if (stripped === "/") {
        return `/${lang}`;
    }

    return `/${lang}${stripped}`;
};

export const parseAcceptLanguage = (headerValue?: string | null): Language => {
    if (!headerValue) {
        return DEFAULT_LANGUAGE;
    }

    const languages = headerValue
        .split(",")
        .map((entry) => entry.trim().split(";")[0]);

    for (const entry of languages) {
        const normalized = normalizeLanguage(entry);
        if (isSupportedLanguage(normalized)) {
            return normalized;
        }
    }

    return DEFAULT_LANGUAGE;
};
